import _ from 'underscore';
import React, { useContext, useMemo } from 'react';
import { Badge, Text, makeStyles, shorthands, mergeClasses } from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Collapse, ValidatedPanel, collapseExpander, getPanelErrorProps, useCollapseState } from '@bingads-webui-campaign/wizard-jumbo-card';
import { DiagnosticsTypes } from './constants';
import { DiagnosticBar } from './diagnostic-bar';
import { SummaryDiagnosticProps } from './types';

const useDiagnosticsSectionStyles = makeStyles({
  diagnosticsSection: {
    maxWidth: '928px',
    '& .collapse-panel': {
      ...shorthands.borderRadius('6px'),
    },
    '& .collapse-title-bar': {
      minHeight: 'fit-content',
      ...shorthands.padding('12px', '0px'),
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    ...shorthands.gap('8px'),
    alignItems: 'center',
  },
});

export function SummaryDiagnostic({ issues, recommendations, onActionClick }: SummaryDiagnosticProps) {
  const classes = useDiagnosticsSectionStyles();
  const i18n = useContext(I18nContext) as any;

  const [
    diagnosticsSection,
    diagnosticsExpandedSection,
    setDiagnosticsExpandedSection,
  ] = useCollapseState(
    ['issues', 'recommendations'] /* all sections */,
    ['issues', 'recommendations'] /* initially expanded sections */
  );
  const diagnosticsSectionExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setDiagnosticsExpandedSection }),
    [setDiagnosticsExpandedSection]
  );

  return (
    <Collapse className={mergeClasses(classes.diagnosticsSection, 'diagnostics-section')} allPanelItems={diagnosticsSection} checkItems skipItemsTypeCheck activeKeys={diagnosticsExpandedSection} setActiveKeys={setDiagnosticsExpandedSection}>
      {issues.length > 0 && (
        <ValidatedPanel
          key="issues"
          title={
            <div className={mergeClasses(classes.header, 'issue-panel')}>
              <Text weight="bold" size={400} className="title">{i18n.getString(_TL_('Campaign issues'))}</Text>
              <Badge className={mergeClasses('issue-badge', 'count')} shape="rounded" appearance="tint" color="informative" size="large">
                {issues.length === 1 ? i18n.getString(_TL_('1 issue')) : i18n.getString(_TL_('{{count}} issues'), { count: `${issues.length}` })}
              </Badge>
            </div>
          }
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
            expand: diagnosticsSectionExpand,
            expandKey: 'issues',
          }}
        >
          <Text className="description">{i18n.getString(_TL_('Fix the issues that are preventing your campaign from running smoothly.'))}</Text>
          <DiagnosticBar itemList={issues} type={DiagnosticsTypes.Issue} onActionClick={onActionClick} />
        </ValidatedPanel>
      )}
      {recommendations.length > 0 && (
        <ValidatedPanel
          key="recommendations"
          title={
            <div className={mergeClasses(classes.header, 'recommendation-panel')}>
              <Text weight="bold" size={400} className="title">{i18n.getString(_TL_('Recommendations'))}</Text>
              <Badge className={mergeClasses('recommendation-badge', 'count')} shape="rounded" appearance="tint" color="informative" size="large">
                {recommendations.length === 1 ? i18n.getString(_TL_('1 recommendation')) : i18n.getString(_TL_('{{count}} recommendations'), { count: `${recommendations.length}` })}
              </Badge>
            </div>
          }
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
            expand: diagnosticsSectionExpand,
            expandKey: 'recommendations',
          }}
        >
          <Text className="description">{i18n.getString(_TL_("Improve your campaign's performance by adopting these recommendations."))}</Text>
          <DiagnosticBar itemList={recommendations} type={DiagnosticsTypes.Recommendation} onActionClick={onActionClick} />
        </ValidatedPanel>
      )}
    </Collapse>
  );
}

SummaryDiagnostic.defaultProps = {
  issues: [],
  recommendations: [],
  onActionClick: _.noop,
};
